import { useCallback, useEffect, useState } from 'react';

// Web Admin page editor → Documents: the PDFs pages link to, like the rule
// books (backend/site_documents.py). Each has a permanent address,
// /documents/<slug>.pdf, that always opens the version uploaded last. Here
// an editor inserts a link to one into the page's text, uploads a new
// version, goes back to an earlier one, adds a new document, or deletes one
// (only when no page links to it).

const MAX_BYTES = 20 * 1024 * 1024;

function when(iso) {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function size(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// As site_documents.slugify: "NHCA Rules Handbook" → "nhca-rules-handbook".
function slugify(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '');
}

// Upload a PDF: a new version of `doc`, or (no doc) a new document.
function UploadForm({ doc, session, onExpired, onDone, onCancel }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const adding = !doc;

  const pick = e => {
    const f = e.target.files[0] || null;
    setError(f && f.size > MAX_BYTES ? 'That file is over 20 MB.' : '');
    setFile(f);
  };

  const changeTitle = value => {
    setTitle(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const upload = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const form = new FormData();
    form.append('pdf', file);
    form.append('version_label', label);
    if (adding) {
      form.append('title', title);
      form.append('slug', slug);
    }
    const res = await fetch(adding ? '/api/site-admin/documents' : `/api/site-admin/documents/${doc.slug}`, {
      method: 'POST', headers: { Authorization: `Bearer ${session.session_token}` }, body: form,
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    setSaving(false);
    if (res && res.ok) onDone();
    else setError(data.error || 'Could not upload. Please try again.');
  };

  const ready = file && file.size <= MAX_BYTES && label.trim() && (!adding || (title.trim() && slug));

  return (
    <form className="site-admin-doc-upload" onSubmit={upload}>
      {adding && (
        <div className="site-admin-pair">
          <label className="site-field">
            <span>Name <span className="site-admin-help">(e.g. &ldquo;AGM minutes 2026&rdquo;)</span></span>
            <input type="text" value={title} maxLength={120} onChange={e => changeTitle(e.target.value)} />
          </label>
          <label className="site-field">
            <span>Address <span className="site-admin-help">(can&apos;t be changed later)</span></span>
            <input type="text" value={slug} maxLength={60}
              onChange={e => { setSlug(e.target.value.toLowerCase()); setSlugEdited(true); }} />
            {slug && <span className="site-admin-help">/documents/{slug}.pdf</span>}
          </label>
        </div>
      )}
      <div className="site-admin-pair">
        <label className="site-field">
          <span>{adding ? 'PDF' : 'New version'} (up to 20 MB)</span>
          <input type="file" accept="application/pdf,.pdf" onChange={pick} />
        </label>
        <label className="site-field">
          <span>Version <span className="site-admin-help">(e.g. &ldquo;2.2, March 2027&rdquo;)</span></span>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)} maxLength={80} />
        </label>
      </div>
      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">The website shows it as soon as it&apos;s uploaded.</span>
        {error && <span className="site-field-error" role="alert">{error}</span>}
        <button type="button" className="site-link-button" onClick={onCancel}>Cancel</button>
        <button type="submit" className="site-btn site-btn--ink" disabled={!ready || saving}>
          {saving ? 'Uploading…' : adding ? 'Add document' : 'Upload'}
        </button>
      </div>
    </form>
  );
}

function DocumentRow({ doc, session, onExpired, reload, onInsert }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const current = doc.versions.find(v => v.id === doc.current_id);
  const auth = { Authorization: `Bearer ${session.session_token}` };

  // Older versions need the login, so they're fetched and shown from memory.
  // The tab is opened first, while the click still counts, so it isn't
  // blocked as a pop-up.
  const openVersion = async v => {
    const tab = window.open('', '_blank');
    const res = await fetch(`/api/site-admin/documents/${doc.slug}/versions/${v.id}`, { headers: auth }).catch(() => null);
    if (res && res.status === 401) { tab?.close(); onExpired(); return; }
    if (!res || !res.ok) { tab?.close(); setError('Could not open that version.'); return; }
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.replace(url); else window.location.assign(url);
  };

  const makeCurrent = async v => {
    setError('');
    const res = await fetch(`/api/site-admin/documents/${doc.slug}/current`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...auth }, body: JSON.stringify({ version_id: v.id }),
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    if (res && res.ok) reload();
    else setError('Could not change the version. Please try again.');
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${doc.title}" and all its versions? Its address, ${doc.url}, will stop working. This can't be undone.`)) return;
    setError('');
    const res = await fetch(`/api/site-admin/documents/${doc.slug}`, { method: 'DELETE', headers: auth }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    if (res && res.ok) { reload(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    setError(data.error || 'Could not delete it. Please try again.');
  };

  return (
    <li className="site-admin-page-doc">
      <div className="site-admin-page-doc-head">
        <div className="site-admin-doc-main">
          <strong>{doc.title}</strong>
          <span className="site-admin-meta">
            <a href={doc.url} target="_blank" rel="noreferrer">{doc.url}</a>
            {current && <> · version {current.version_label}, uploaded {when(current.uploaded_at)}</>}
            {doc.used_on.length > 0 && <> · linked from {doc.used_on.join(', ')}</>}
          </span>
        </div>
        <div className="site-admin-doc-actions">
          <button type="button" className="site-btn site-btn--ink" onClick={() => onInsert(doc)}>Insert link</button>
          <button type="button" className="site-link-button" aria-expanded={uploading}
            onClick={() => setUploading(u => !u)}>Upload new version</button>
          {doc.used_on.length === 0 && <button type="button" className="site-link-button" onClick={remove}>Delete</button>}
        </div>
      </div>
      {uploading && (
        <UploadForm doc={doc} session={session} onExpired={onExpired}
          onDone={() => { setUploading(false); reload(); }} onCancel={() => setUploading(false)} />
      )}
      {error && <p className="site-field-error" role="alert">{error}</p>}
      {doc.versions.length > 1 && (
        <details className="site-admin-revisions">
          <summary>All versions ({doc.versions.length})</summary>
          <ul>
            {doc.versions.map(v => (
              <li key={v.id}>
                <span><strong>{v.version_label}</strong>{v.id === doc.current_id ? ' (on the website)' : ''}</span>
                <span className="site-admin-meta">{when(v.uploaded_at)}, {v.uploaded_by} · {v.filename} · {size(v.size_bytes)}</span>
                <button type="button" className="site-link-button" onClick={() => openVersion(v)}>Open</button>
                {v.id !== doc.current_id && (
                  <button type="button" className="site-link-button" onClick={() => makeCurrent(v)}>Show this version on the website</button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

// `open`: start open (the page already links to a document).
export default function PageDocuments({ session, onExpired, onInsert, open }) {
  const [docs, setDocs] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetch('/api/site-admin/documents', { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load');
        setDocs(data.documents);
      })
      .catch(e => setError(e.message));
  }, [session, onExpired]);

  useEffect(() => { load(); }, [load]);

  return (
    <details className="site-admin-page-docs" open={open}>
      <summary>Documents (PDFs){docs ? ` · ${docs.length}` : ''}</summary>
      <p className="site-admin-meta">
        PDFs kept on the website, like the rule books. Each has an address that never changes and always opens the latest
        version, so upload a new version here when one comes out and every link to it shows the new one.
        Insert link puts a link at the cursor in the text above. To link to a PDF on another website, use an ordinary link.
        A document can only be deleted when no page (or the notice bar) links to it.
      </p>
      {error && <p className="site-field-error">{error}</p>}
      {!docs && !error && <p>Loading…</p>}
      {docs && (
        <ul className="site-admin-page-doc-list">
          {docs.map(d => (
            <DocumentRow key={d.slug} doc={d} session={session} onExpired={onExpired} reload={load} onInsert={onInsert} />
          ))}
        </ul>
      )}
      {adding ? (
        <UploadForm session={session} onExpired={onExpired}
          onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      ) : (
        <button type="button" className="site-btn site-btn--ghost-dark" onClick={() => setAdding(true)}>Add a document</button>
      )}
    </details>
  );
}
